import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../utils/supabase';
import { downloadCsv } from '../../utils/leads';
import { errorBox, inputClass } from '../AuthLayout';

// Admin → Finance: money in and out by month.
// Stripe side = balance transactions of the active mode (via admin-stripe `finance`); amounts in cents.
// Your side = public.expenses, costs paid outside Stripe; invoice payments (checks etc.) recorded in
// Admin → Invoices count as money in. Profit = Stripe net + invoice payments − expenses.
// Payouts only move money from Stripe to your bank, so they are shown but are not a cost.

const CATEGORIES = [
  ['hosting', 'Hosting'], ['software', 'Software'], ['email', 'Email'], ['nfc_cards', 'NFC cards'],
  ['marketing', 'Marketing'], ['fees', 'Bank & other fees'], ['other', 'Other'],
];
const categoryName = (id) => CATEGORIES.find(([k]) => k === id)?.[1] || 'Other';

const money = (cents) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
const pad = (n) => String(n).padStart(2, '0');
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};
const dayLabel = (d) => d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// A date-only string ('2026-10-14') as local midnight, not UTC
const localDate = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

/** Ranges are [start month, end month] as 'YYYY-MM' (inclusive). */
function presetRange(id) {
  const now = new Date();
  const m = (offset) => monthKey(new Date(now.getFullYear(), now.getMonth() + offset, 1));
  switch (id) {
    case 'last_month': return [m(-1), m(-1)];
    case 'last_3': return [m(-2), m(0)];
    case 'this_year': return [`${now.getFullYear()}-01`, m(0)];
    case 'last_year': return [`${now.getFullYear() - 1}-01`, `${now.getFullYear() - 1}-12`];
    default: return [m(0), m(0)];
  }
}
const PRESETS = [['this_month', 'This month'], ['last_month', 'Last month'], ['last_3', 'Last 3 months'], ['this_year', 'This year'], ['last_year', 'Last year'], ['custom', 'Custom']];

function monthsBetween(from, to) {
  const out = [];
  let [y, m] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${pad(m)}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

/** Which bucket a Stripe balance transaction belongs to (by Stripe's reporting_category). */
function bucket(t) {
  const c = t.category || '';
  if (c === 'charge') return 'sale';
  if (/refund|dispute|partial_capture_reversal/.test(c)) return 'refund';
  if (/payout/.test(c)) return 'payout';
  if (/fee|tax/.test(c)) return 'fee';
  return 'other';
}
const BUCKET_NAME = { sale: 'Sale', refund: 'Refund', fee: 'Stripe fee', payout: 'Payout to bank', other: 'Adjustment', expense: 'Expense', invoice: 'Invoice payment' };

const emptyTotals = () => ({ sales: 0, refunds: 0, fees: 0, other: 0, net: 0, invoices: 0, expenses: 0, profit: 0, payouts: 0 });

function addStripe(tot, t) {
  const b = bucket(t);
  if (b === 'payout') { tot.payouts += -t.amount; return; }
  if (b === 'sale') tot.sales += t.amount;
  else if (b === 'refund') tot.refunds += -t.amount;
  else if (b === 'fee') tot.fees += -t.amount;
  else tot.other += t.amount;
  tot.fees += t.fee; // the fee Stripe took on this transaction
  tot.net += t.net;
}

async function stripeFinance(from, to) {
  const { data, error } = await supabase.functions.invoke('admin-stripe', { body: { action: 'finance', from, to } });
  if (error) {
    let message = error.message;
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* keep generic */ }
    throw new Error(message);
  }
  return data;
}

export default function Finance() {
  const [preset, setPreset] = useState('this_year');
  const [range, setRange] = useState(() => presetRange('this_year'));
  const [stripe, setStripe] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [invoicePayments, setInvoicePayments] = useState(null);
  const [error, setError] = useState('');
  const [stripeError, setStripeError] = useState('');
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('all');

  const [fromMonth, toMonth] = range[0] <= range[1] ? range : [range[1], range[0]];
  const start = localDate(`${fromMonth}-01`);
  const [ty, tm] = toMonth.split('-').map(Number);
  const end = new Date(ty, tm, 1); // first day after the range

  const load = useCallback(async () => {
    setError('');
    setStripeError('');
    setStripe(null);
    setExpenses(null);
    setInvoicePayments(null);
    const [s, e, ip] = await Promise.allSettled([
      stripeFinance(Math.floor(start.getTime() / 1000), Math.floor(end.getTime() / 1000)),
      supabase.from('expenses').select('*').gte('spent_on', isoDay(start)).lt('spent_on', isoDay(end)).order('spent_on', { ascending: false }),
      supabase.from('invoice_payments').select('id, amount_cents, received_on, method, reference, invoices(number, invoice_clients(name))')
        .gte('received_on', isoDay(start)).lt('received_on', isoDay(end)),
    ]);
    if (s.status === 'fulfilled') setStripe(s.value);
    else { setStripeError(s.reason.message); setStripe({ rows: [], balance: null }); }
    if (e.status === 'fulfilled' && !e.value.error) setExpenses(e.value.data);
    else { setError(e.value?.error?.message || e.reason?.message || 'Could not load expenses.'); setExpenses([]); }
    if (ip.status === 'fulfilled' && !ip.value.error) setInvoicePayments(ip.value.data);
    else { setError(ip.value?.error?.message || ip.reason?.message || 'Could not load invoice payments.'); setInvoicePayments([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromMonth, toMonth]);

  useEffect(() => { load(); }, [load]);

  const loading = stripe === null || expenses === null || invoicePayments === null;

  const report = useMemo(() => {
    if (loading) return null;
    const months = monthsBetween(fromMonth, toMonth);
    const byMonth = Object.fromEntries(months.map((k) => [k, emptyTotals()]));
    const total = emptyTotals();
    const usd = (stripe.rows || []).filter((t) => t.currency === 'usd');
    for (const t of usd) {
      const k = monthKey(new Date(t.created * 1000));
      if (byMonth[k]) addStripe(byMonth[k], t);
      addStripe(total, t);
    }
    for (const x of expenses) {
      const k = x.spent_on.slice(0, 7);
      if (byMonth[k]) byMonth[k].expenses += x.amount_cents;
      total.expenses += x.amount_cents;
    }
    for (const p of invoicePayments) {
      const k = p.received_on.slice(0, 7);
      if (byMonth[k]) byMonth[k].invoices += p.amount_cents;
      total.invoices += p.amount_cents;
    }
    for (const t of [...Object.values(byMonth), total]) t.profit = t.net + t.invoices - t.expenses;

    const lines = [
      ...usd.map((t) => ({
        key: t.id, date: new Date(t.created * 1000), kind: bucket(t), id: t.id,
        description: [t.description, t.email].filter(Boolean).join(' · ') || t.type,
        amount: t.amount, fee: t.fee, net: t.net, pending: t.status === 'pending',
      })),
      ...expenses.map((x) => ({
        key: x.id, date: localDate(x.spent_on), kind: 'expense', id: x.id, expense: x,
        description: [x.vendor, categoryName(x.category), x.description].filter(Boolean).join(' · '),
        amount: -x.amount_cents, fee: 0, net: -x.amount_cents,
      })),
      ...invoicePayments.map((p) => ({
        key: p.id, date: localDate(p.received_on), kind: 'invoice', id: p.invoices?.number || p.id,
        description: [`Invoice ${p.invoices?.number || ''}`, p.invoices?.invoice_clients?.name, p.reference ? `${p.method} #${p.reference}` : p.method].filter(Boolean).join(' · '),
        amount: p.amount_cents, fee: 0, net: p.amount_cents,
      })),
    ].sort((a, b) => b.date - a.date);
    const otherCurrencies = (stripe.rows || []).length - usd.length;
    return { months: months.slice().reverse(), byMonth, total, lines, otherCurrencies };
  }, [loading, stripe, expenses, invoicePayments, fromMonth, toMonth]);

  const shown = report?.lines.filter((l) => filter === 'all' || (filter === 'stripe' ? !['expense', 'invoice'].includes(l.kind) : l.kind === filter)) ?? [];

  const exportCsv = () => {
    const rows = report.lines.map((l) => [
      isoDay(l.date), BUCKET_NAME[l.kind], l.description,
      l.amount / 100, l.fee / 100, l.net / 100, l.kind === 'expense' ? 'Expense' : l.kind === 'invoice' ? 'Invoice' : 'Stripe', l.id,
    ]);
    downloadCsv(['Date', 'Type', 'Description', 'Amount (USD)', 'Stripe fee', 'Net', 'Source', 'Reference'], rows, `carduzo-finance-${fromMonth}-to-${toMonth}.csv`);
  };

  const removeExpense = async (x) => {
    if (!window.confirm(`Delete the ${money(x.amount_cents)} expense from ${x.vendor}?`)) return;
    const { error } = await supabase.from('expenses').delete().eq('id', x.id);
    if (error) setError(error.message);
    else setExpenses((list) => list.filter((e) => e.id !== x.id));
  };

  const balance = stripe?.balance;
  const bal = (list) => (list || []).filter((b) => b.currency === 'usd').reduce((s, b) => s + b.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <label>
          <span className="block text-sm font-medium text-ink mb-1">Period</span>
          <select className={inputClass} value={preset} onChange={(e) => {
            setPreset(e.target.value);
            if (e.target.value !== 'custom') setRange(presetRange(e.target.value));
          }}>
            {PRESETS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>
        {preset === 'custom' && (
          <>
            <label>
              <span className="block text-sm font-medium text-ink mb-1">From</span>
              <input type="month" className={inputClass} value={range[0]} onChange={(e) => e.target.value && setRange([e.target.value, range[1]])} />
            </label>
            <label>
              <span className="block text-sm font-medium text-ink mb-1">To</span>
              <input type="month" className={inputClass} value={range[1]} onChange={(e) => e.target.value && setRange([range[0], e.target.value])} />
            </label>
          </>
        )}
        <span className="flex-1" />
        <button type="button" onClick={() => setAdding(true)} className="h-11 px-4 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark">Add expense</button>
        <button type="button" onClick={exportCsv} disabled={!report}
          className="h-11 px-4 rounded-xl border border-line-strong bg-white text-ink font-semibold hover:bg-surface disabled:opacity-50">Download CSV</button>
      </div>

      {error && <div className={errorBox}>{error}</div>}
      {stripeError && <div className={errorBox}>Stripe: {stripeError}</div>}
      {adding && <AddExpense onCancel={() => setAdding(false)} onSaved={(x) => { setAdding(false); load(); return x; }} />}

      {stripe?.mode === 'test' && (
        <div className="bg-coral-soft border border-coral/40 text-ink px-4 py-3 rounded-lg text-sm">
          Stripe is in sandbox mode, so these Stripe numbers are test money.
        </div>
      )}
      {stripe?.truncated && <div className="text-sm text-ink-muted">Showing the newest 2,000 Stripe transactions only. Pick a shorter period for exact totals.</div>}
      {report?.otherCurrencies > 0 && <div className="text-sm text-ink-muted">{report.otherCurrencies} non-USD Stripe transactions are not included in the totals.</div>}

      {loading ? (
        <div className="bg-white rounded-2xl border border-line px-4 py-10 text-center text-ink-muted">Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            <Tile label="Sales" value={report.total.sales} />
            <Tile label="Refunds" value={-report.total.refunds} />
            <Tile label="Stripe fees" value={-report.total.fees} />
            <Tile label="Stripe net" value={report.total.net} strong />
            <Tile label="Invoice payments" value={report.total.invoices} note="Checks and other payments on invoices" />
            <Tile label="Your expenses" value={-report.total.expenses} />
            <Tile label="Profit" value={report.total.profit} strong />
            <Tile label="Paid out to your bank" value={report.total.payouts} muted />
            <Tile label="Stripe balance now" value={balance ? bal(balance.available) + bal(balance.pending) : null}
              note={balance ? `${money(bal(balance.available))} available · ${money(bal(balance.pending))} pending` : 'Unavailable'} muted />
          </div>

          <div className="bg-white rounded-2xl border border-line overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-ink-muted">
                <tr>
                  {['Month', 'Sales', 'Refunds', 'Stripe fees', 'Stripe net', 'Invoices', 'Expenses', 'Profit', 'Payouts'].map((h, i) => (
                    <th key={h} className={`px-4 py-3 font-medium ${i ? 'text-right' : ''}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line tabular-nums">
                {report.months.map((k) => {
                  const m = report.byMonth[k];
                  return (
                    <tr key={k}>
                      <td className="px-4 py-3 font-medium text-ink whitespace-nowrap">{monthLabel(k)}</td>
                      <Num v={m.sales} /><Num v={-m.refunds} /><Num v={-m.fees} /><Num v={m.net} strong />
                      <Num v={m.invoices} /><Num v={-m.expenses} /><Num v={m.profit} strong /><Num v={m.payouts} muted />
                    </tr>
                  );
                })}
                {report.months.length > 1 && (
                  <tr className="bg-surface font-semibold">
                    <td className="px-4 py-3 text-ink">Total</td>
                    <Num v={report.total.sales} /><Num v={-report.total.refunds} /><Num v={-report.total.fees} /><Num v={report.total.net} strong />
                    <Num v={report.total.invoices} /><Num v={-report.total.expenses} /><Num v={report.total.profit} strong /><Num v={report.total.payouts} muted />
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-semibold text-ink flex-1">Transactions</h2>
              <div className="w-56">
                <select className={inputClass} value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Show">
                  <option value="all">Everything</option>
                  <option value="stripe">Stripe only</option>
                  <option value="invoice">Invoice payments only</option>
                  <option value="expense">Expenses only</option>
                </select>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-line overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Description</th>
                    <th className="px-4 py-3 font-medium text-right">Amount</th>
                    <th className="px-4 py-3 font-medium text-right">Stripe fee</th>
                    <th className="px-4 py-3 font-medium text-right">Net</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line tabular-nums">
                  {shown.length === 0 && <tr><td colSpan="7" className="px-4 py-6 text-center text-ink-muted">Nothing in this period.</td></tr>}
                  {shown.map((l) => (
                    <tr key={l.key}>
                      <td className="px-4 py-3 whitespace-nowrap">{dayLabel(l.date)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {BUCKET_NAME[l.kind]}
                        {l.pending && <span className="ml-2 text-xs text-ink-muted">pending</span>}
                      </td>
                      <td className="px-4 py-3 max-w-[420px] truncate" title={l.description}>{l.description}</td>
                      <Num v={l.amount} />
                      <Num v={l.fee ? -l.fee : null} />
                      <Num v={l.net} strong />
                      <td className="px-4 py-3 text-right">
                        {l.expense && <button type="button" onClick={() => removeExpense(l.expense)} className="text-ink-muted hover:underline">Delete</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, note, strong, muted }) {
  return (
    <div className="bg-white rounded-2xl border border-line p-4">
      <div className="text-sm text-ink-muted">{label}</div>
      <div className={`mt-1 text-[22px] font-extrabold tabular-nums ${muted ? 'text-ink-muted' : value < 0 && strong ? 'text-coral-ink' : 'text-ink'}`}>
        {value == null ? '—' : money(value)}
      </div>
      {note && <div className="text-xs text-ink-muted mt-1">{note}</div>}
    </div>
  );
}

function Num({ v, strong, muted }) {
  return (
    <td className={`px-4 py-3 text-right whitespace-nowrap ${strong ? 'font-semibold' : ''} ${muted || !v ? 'text-ink-muted' : v < 0 ? 'text-coral-ink' : 'text-ink'}`}>
      {v == null ? '' : money(v)}
    </td>
  );
}

function AddExpense({ onCancel, onSaved }) {
  const [form, setForm] = useState({ spent_on: isoDay(new Date()), vendor: '', category: 'hosting', description: '', amount: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    const cents = Math.round(Number(String(form.amount).replace(/[$,\s]/g, '')) * 100);
    if (!cents || cents <= 0) { setError('Enter the amount, for example 25.00.'); return; }
    setSaving(true);
    setError('');
    const { data, error } = await supabase.from('expenses').insert({
      spent_on: form.spent_on, vendor: form.vendor.trim(), category: form.category,
      description: form.description.trim() || null, amount_cents: cents,
    }).select().single();
    setSaving(false);
    if (error) setError(error.message);
    else onSaved(data);
  };

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-line p-6 space-y-4">
      <h2 className="font-semibold text-ink">Add an expense</h2>
      <p className="text-sm text-ink-muted -mt-2">Costs paid outside Stripe, like Supabase, Netlify, Resend or card printing. Stripe's own fees are counted automatically.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label><span className="block text-sm font-medium text-ink mb-1">Date</span>
          <input type="date" className={inputClass} value={form.spent_on} onChange={set('spent_on')} required disabled={saving} /></label>
        <label><span className="block text-sm font-medium text-ink mb-1">Paid to</span>
          <input className={inputClass} value={form.vendor} onChange={set('vendor')} placeholder="Netlify" required maxLength={120} disabled={saving} /></label>
        <label><span className="block text-sm font-medium text-ink mb-1">Category</span>
          <select className={inputClass} value={form.category} onChange={set('category')} disabled={saving}>
            {CATEGORIES.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select></label>
        <label><span className="block text-sm font-medium text-ink mb-1">Amount (USD)</span>
          <input inputMode="decimal" className={inputClass} value={form.amount} onChange={set('amount')} placeholder="19.00" required disabled={saving} /></label>
      </div>
      <label className="block"><span className="block text-sm font-medium text-ink mb-1">Note (optional)</span>
        <input className={inputClass} value={form.description} onChange={set('description')} maxLength={500} placeholder="Pro plan, October" disabled={saving} /></label>
      {error && <div className={errorBox}>{error}</div>}
      <div className="flex gap-2.5">
        <button type="submit" disabled={saving} className="h-11 px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark disabled:opacity-60">{saving ? 'Saving...' : 'Save expense'}</button>
        <button type="button" onClick={onCancel} disabled={saving} className="h-11 px-4 rounded-xl border border-line-strong bg-white text-ink font-semibold hover:bg-surface">Cancel</button>
      </div>
    </form>
  );
}
